<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import EnEditableDataFieldsViewSection from 'Components/forms/EnEditableDataFieldsViewSection/EnEditableDataFieldsViewSection.vue';
import { enFetch } from 'Js/utils/fetchUtils';

const {t} = useI18n();
const emit = defineEmits(['message-receive']);


const user = ref({});
const isEditing = ref(false);
const forceSave = ref(false);

function onLocalFieldsChanged(local) {
  const hasOld = !!((local.find(f => f.key === 'OldPassword') || {}).data);
  const hasNew = !!((local.find(f => f.key === 'NewPassword') || {}).data);
  const hasConfirm = !!((local.find(f => f.key === 'ConfirmPassword') || {}).data);
  forceSave.value = hasOld && hasNew && hasConfirm;
}

const fields = computed({
  get() {
    const userData = user.value || {};
    if (!isEditing.value) {
      return [
        {
          key: 'OldPassword',
          fieldLabel: t('Authentication.manage.password.oldPassword'),
          data: userData.OldPassword ? '••••••••' : '',
          editable: false,
          type: 'password'
        },
        {key: 'Role', fieldLabel: t('Authentication.manage.general.userRole') || 'Role', data: userData.Role || 'EN.Facts Admin', editable: false}
      ];
    }

    return [
      {key: 'OldPassword', fieldLabel: t('Authentication.manage.password.oldPassword'), data: userData.OldPassword || '', editable: true, type: 'password'},
      {key: 'NewPassword', fieldLabel: t('Authentication.manage.password.newPassword'), data: userData.NewPassword || '', editable: true, type: 'password'},
      {
        key: 'ConfirmPassword',
        fieldLabel: t('Authentication.manage.password.confirmNewPassword'),
        data: userData.ConfirmPassword || '',
        editable: true,
        type: 'password',
        rules: [
          (val, localFields) => {
            const newField = (localFields || []).find(f => f.key === 'NewPassword');
            const newVal = newField ? newField.data : '';
            return (val === newVal) || t('Authentication.manage.password.passwordsDoNotMatch');
          }
        ]
      },
    ];
  },
  set(newFields) {
    const updated = {...(user.value || {})};
    newFields.forEach(f => {
      if (f.key) updated[f.key] = f.data;
    });
    user.value = updated;
  }
});

const cannotBeSaved = computed(() => {
  if (!user.value || !user.value.OldPassword) return true;

  const newPw = user.value.NewPassword;
  const confirm = user.value.ConfirmPassword;

  if (!newPw && !confirm) return false;

  if (!newPw || !confirm) return true;
  return newPw !== confirm;
});

async function save() {
  const res = await enFetch({url: '/api/Profile/SavePassword', method: 'post', body: JSON.stringify(user.value)});
  emit('message-receive', await res.text(), res.ok ? 'success' : 'error');
}

async function onUpdateFields(updated) {
  fields.value = updated;
  if (cannotBeSaved.value) {
    emit('message-receive', t('Authentication.manage.password.passwordsDoNotMatch'));
    return;
  }
  await save();
}

function onValidationFailed(errors) {
  emit('message-receive', (errors || []).map(e => e.message).join(', '), 'error');
}

function onEditButtonClicked() {
  isEditing.value = true;
}
</script>

<template>
  <div class="password-container">
    <en-editable-data-fields-view-section v-model:is-editing="isEditing" :fields="fields"
                                          :section-heading="t('Authentication.manage.password.title')"
                                          :save-disabled-override="isEditing ? !forceSave : undefined" @update-fields="onUpdateFields"
                                          @validation-failed="onValidationFailed" @edit-button-clicked="onEditButtonClicked"
                                          @local-fields-changed="onLocalFieldsChanged"
                                          class="user-password-fields"
    ></en-editable-data-fields-view-section>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";

.password-container {
  max-width: 1200px;
  width: 100%;
  gap: 3rem;
  margin-top: variables.$spacing-l;
  display: flex;
  flex-direction: column;
}

:deep(.user-password-fields .v-text-field) {
  padding: 0 !important;
}

:deep(.v-input__details) {
  display: none !important;
}

:deep(.user-password-fields .fields-view-container) {
  grid-template-columns: max-content minmax(300px, 40%);
  column-gap: 3rem;
}

:deep(.user-password-fields .field-label) {
  padding-right: 1.5rem;
}

:deep(.user-password-fields .field-key-Role) {
  color: variables.$en-70;
  background: transparent;
}

@media (max-width: 640px) {
  :deep(.user-password-fields .fields-view-container) {
    grid-template-columns: 1fr;
    row-gap: 0.5rem;
    padding: 0.5rem;
  }

  :deep(.user-password-fields .field-label) {
    white-space: normal;
    margin-bottom: 0.125rem;
    padding-right: 0.125rem;
  }
  :deep(.user-password-fields .field-data) {
    white-space: normal;
    margin-bottom: 0 !important;
  }
}
</style>